import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { EntAgroculturalCooperative } from "../../../../models/mechanization/agroculturalCooperative";
import { useAddAgroculturalCooperativeMutation, useDeleteAgroculturalCooperativeMutation, useEditAgroculturalCooperativeMutation } from "../../../../redux/api/mechanization/agroculturalCooperativeApi";
import { getCookie } from "typescript-cookie";

const useAgroculturalCooperative = (modal: any, refresh?: () => void) => {
    const { t } = useTranslation();
    const { confirm } = modal;
    const [addAgroculturalCooperative] = useAddAgroculturalCooperativeMutation();
    const [deleteAgroculturalCooperative] = useDeleteAgroculturalCooperativeMutation();
    const [editAgroculturalCooperative] = useEditAgroculturalCooperativeMutation();

    async function onAddAgroculturalCooperative(newAgroculturalCooperative: EntAgroculturalCooperative): Promise<boolean> {
        try {
            let status: boolean = false;
            await addAgroculturalCooperative(newAgroculturalCooperative).then((response: any) => {
                if (response.error !== undefined) {
                    const isKyrgyz = getCookie('i18next') === 'ky';
                    const lang = isKyrgyz ? 'kg' : 'ru';
                    const res = response.error.data?.res;
                    const errorMessages = Array.isArray(res)
                        ? res.map((error: any) => error[lang] || error.ru).join('\n')
                        : response.error.data?.msg || t('unknownError'); 
                    throw new Error(errorMessages);
                }
                if (response.data.success) {
                    status = true;
                    message.success(t('msg.successAdd'));
                    if (refresh) refresh();
                }
            }).catch((error: any) => {
                modal.error({ title: t('msg.error'), content: String(error.message) });
            });
            return status;
        } catch (error: any) {
            modal.error({ title: t('msg.error'), content: String(error.message) });
            return false;
        }
    }

    async function onDeleteAgroculturalCooperative(agroculturalCooperative: EntAgroculturalCooperative) {
        try {
            let status: boolean = false;
            confirm({
                centered: true,
                title: t('msg.confirmDeleteText'),
                okText: t('general.yes'),
                cancelText: t('general.no'),
                onOk() {
                    deleteAgroculturalCooperative(agroculturalCooperative.id as number).then((response: any) => {
                        if (response.error) {
                            const isKyrgyz = getCookie('i18next') === 'ky';
                            const lang = isKyrgyz ? 'kg' : 'ru';
                            const res = response.error.data?.res;
                            const errorMessages = Array.isArray(res)
                                ? res.map((error: any) => error[lang] || error.ru).join('\n')
                                : response.error.data?.msg || t('unknownError'); 
                            throw new Error(errorMessages);
                        }
                        if (response.data.success) {
                            status = true;
                            Modal.success({ title: t('msg.successDelete') });
                            if (refresh) refresh();
                        }
                    }).catch((error: any) => {
                        Modal.error({ title: t('msg.error'), content: String(error.message) });
                    });
                }
            });
            return status;
        } catch (error: any) {
            Modal.error({ title: t("msg.error"), content: String(error.message) });
            return false;
        }
    };

    async function onEditAgroculturalCooperative(editedAgroculturalCooperative: EntAgroculturalCooperative): Promise<boolean> {
        try {
            let status: boolean = false;
            await editAgroculturalCooperative(editedAgroculturalCooperative).then((response: any) => {
                if (response.error) {
                    const isKyrgyz = getCookie('i18next') === 'ky';
                    const lang = isKyrgyz ? 'kg' : 'ru';
                    const res = response.error.data?.res;
                    const errorMessages = Array.isArray(res)
                        ? res.map((error: any) => error[lang] || error.ru).join('\n')
                        : response.error.data?.msg || t('unknownError'); 
                    throw new Error(errorMessages);
                }
                if (response.data.success) {
                    status = true;
                    Modal.success({ title: t('msg.successEdited') });
                    if (refresh) refresh();
                }
            }).catch((error: any) => {
                Modal.error({ title: t('msg.error'), content: String(error.message) });
            });
            return status;
        } catch (error: any) {
            Modal.error({ title: t("msg.error"), content: String(error.message) });
            return false;
        }
    };

    return { onAddAgroculturalCooperative, onDeleteAgroculturalCooperative, onEditAgroculturalCooperative };
};

export { useAgroculturalCooperative };
